import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Context } from '../controls/advanced-gridview/models/context';
import { FilterInfos } from '../controls/advanced-gridview/models/filter-infos';
import { Results } from '../controls/advanced-gridview/models/results-T';
import { SortInfos } from '../controls/advanced-gridview/models/sort-infos';
import { AdvancedService } from '../controls/advanced-gridview/services/advanced-service';
import { BatchModification } from '../controls/inline-editable-table/models/batch-modifications';
import { BatchResult } from '../controls/inline-editable-table/models/batch-result';
import { BatchEditableTableServiceInterface } from '../controls/inline-editable-table/services/batch-editable-table-service-interface';
import { BoxAccessibility } from '../models/box-accessibility';
import { IBoxAccessibility } from '../models/interfaces/box-accessibility.interface';
import { User } from '../models/user';
import { CrudService } from './crud-service';
import { MemoryService } from './memory.service';
import { EnumReasonResetPassword } from '../models/enums/enum-reason-reset-password';
import { EnumModeResetPassword } from '../models/enums/enum-mode-reset-password';
import { ViewAccessibility } from '../models/view-accessibility';
import { IViewAccessibility } from '../models/interfaces/view-accessibility.interface';

@Injectable({
    providedIn: 'root',
})
export class UsersService extends CrudService implements AdvancedService<User>, BatchEditableTableServiceInterface<User> {
    constructor(
        private http: HttpClient,
        public override memoryService: MemoryService
    ) {
        super(memoryService);
    }

    List(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos, pageNumber = 0, pageSize = 100): Observable<Results<User>> {
        let params = new Map<string, string | number | boolean>();
        params.set('from', pageNumber);
        params.set('size', pageSize);

        const httpOptions = this.getListHttpOptions(context, filters, generalFilters, sortInfos, params);

        if (context === null) {
            // Appel de la Web Api
            return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/Users`, httpOptions).pipe(
                map((x) => {
                    console.log('UsersService List', x);
                    var res = Results.getCopyFrom<User>(User, x);
                    return res;
                })
            );
        } else {
            // Appel de la Web Api
            return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/Group/${context.value}/users`, httpOptions).pipe(
                map((x) => {
                    var res = Results.getCopyFrom<User>(User, x);
                    return res;
                })
            );
        }
    }

    Export(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos): Observable<Blob> {
        const headers = this.getExportHeader(context, filters, generalFilters, sortInfos);

        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/Users/export`, { headers: headers, responseType: 'blob' }).pipe(
            map((res: any) => {
                const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                return blob;
            })
        );
    }

    update(userId: string, user: User): Observable<User> {
        // Appel de la Web Api
        return this.http.put<User>(this.baseUrl + `/api/Organizations/${this.organizationId}/Users/${userId}`, user, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return x;
            })
        );
    }

    reset(userId: string): Observable<User> {
        // Appel de la Web Api
        return this.http.delete<User>(this.baseUrl + `/api/Organizations/${this.organizationId}/Users/${userId}`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return x;
            })
        );
    }

    add(user: User): Observable<User> {
        // Appel de la Web Api
        return this.http.post<User>(this.baseUrl + `/api/Organizations/${this.organizationId}/Users`, user, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return x;
            })
        );
    }

    get(userId: string): Observable<User> {
        // Appel de la Web Api
        return this.http.get<User>(this.baseUrl + `/api/Organizations/${this.organizationId}/Users/${userId}`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return x;
            })
        );
    }

    getBoxes(userId: string): Observable<BoxAccessibility[]> {
        // Appel de la Web Api
        return this.http.get<BoxAccessibility[]>(this.baseUrl + `/api/Organizations/${this.organizationId}/Users/${userId}/Boxes`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return x;
            })
        );
    }

    getInheritedBoxes(userId: string): Observable<BoxAccessibility[]> {
        let options = this.getParameterizedHttpOptions([{ key: 'inheritedOnly', value: 'true' }]);

        // Appel de la Web Api
        return this.http.get<BoxAccessibility[]>(this.baseUrl + `/api/Organizations/${this.organizationId}/Users/${userId}/Boxes`, options).pipe(
            map((x) => {
                return x;
            })
        );
    }

    exportBoxes(userId: string): Observable<Blob> {
        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/Users/${userId}/Boxes/export`, { responseType: 'blob' }).pipe(
            map((res: any) => {
                const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                return blob;
            })
        );
    }

    updateBoxes(userId: string, boxesAccessibility: IBoxAccessibility[]): Observable<boolean> {
        // Appel de la Web Api
        return this.http.put(this.baseUrl + `/api/Organizations/${this.organizationId}/Users/${userId}/Boxes`, boxesAccessibility, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return true;
            })
        );
    }

    public applyBatchModifications(context: Context, modifications: BatchModification<User>): Observable<BatchResult[]> {
        let requests = new Array<Observable<BatchResult>>();

        modifications.deletes.forEach((x) => {
            requests.push(this.reset(x.data.id).pipe(map((y) => new BatchResult(x.data.id, true))));
        });

        modifications.updates.forEach((x) => {
            requests.push(this.update(x.data.id, x.data).pipe(map((y) => new BatchResult(x.data.editableId, true))));
        });

        modifications.creates.forEach((x) => {
            requests.push(this.add(x.data).pipe(map((y) => new BatchResult(x.data.id, true))));
        });

        return forkJoin(requests);
    }

    StartPasswordReset(userId: string, reason: EnumReasonResetPassword, mode: EnumModeResetPassword, inputUrl: string, timeout: number = null): Observable<string | null> {
        let httpOptions = {
            headers: new HttpHeaders().set('Content-Type', 'application/json'),
            params: new HttpParams().set('reason', reason).set('mode', mode).set('inputUrl', inputUrl),
        };

        if (timeout) {
            httpOptions.params = httpOptions.params.append('timeout', timeout);
        }

        return this.http.post<string>(`${this.baseUrl}/api/users/${userId}/resetPassword`, null, httpOptions);
    }

    getViews(userId: string): Observable<ViewAccessibility[]> {
        // Appel de la Web Api
        return this.http
            .get<ViewAccessibility[]>(this.baseUrl + `/api/Organizations/${this.organizationId}/Users/${userId}/viewsAccessibilities`, this.getDefaultHttpOptions())
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    getInheritedViews(userId: string): Observable<ViewAccessibility[]> {
        let options = this.getParameterizedHttpOptions([{ key: 'inheritedOnly', value: 'true' }]);

        // Appel de la Web Api
        return this.http.get<ViewAccessibility[]>(this.baseUrl + `/api/Organizations/${this.organizationId}/Users/${userId}/viewsAccessibilities`, options).pipe(
            map((x) => {
                return x;
            })
        );
    }

    updateViews(userId: string, viewsAccessibility: IViewAccessibility[]): Observable<boolean> {
        // Appel de la Web Api
        return this.http
            .put(this.baseUrl + `/api/Organizations/${this.organizationId}/Users/${userId}/viewsAccessibilities`, viewsAccessibility, this.getDefaultHttpOptions())
            .pipe(
                map((x) => {
                    return true;
                })
            );
    }

    exportViews(userId: string): Observable<Blob> {
        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/Users/${userId}/viewsAccessibilities/export`, { responseType: 'blob' }).pipe(
            map((res: any) => {
                const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                return blob;
            })
        );
    }
}
